import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaComment, FaPaperPlane, FaSpinner, FaCalendarAlt, FaClock, FaCompactDisc } from 'react-icons/fa';
import axios from 'axios';
import API_BASE_URL from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import './BookingForm.css';

const BookingForm = ({ selectedDate, selectedTimeStart, selectedTimeEnd, onSuccess }) => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    project: 'commercial',
    location: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Валидация
    if (!formData.name.trim() || !formData.email.trim()) {
      setError(t('contacts.sendMessage') + ': ' + t('booking.name') + ', ' + t('booking.email'));
      return;
    }

    if (!selectedTimeStart || !selectedTimeEnd) {
      setError(t('booking.startTime') + ' / ' + t('booking.endTime'));
      return;
    }

    // Проверка email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('contacts.yourEmail'));
      return;
    }

    // Проверка времени (начало должно быть раньше конца, учитывая переход через полночь)
    if (selectedTimeStart && selectedTimeEnd) {
      const [startHours, startMinutes] = selectedTimeStart.split(':').map(Number);
      const [endHours, endMinutes] = selectedTimeEnd.split(':').map(Number);
      
      // Проверяем, что значения валидны
      if (isNaN(startHours) || isNaN(startMinutes) || isNaN(endHours) || isNaN(endMinutes)) {
        setError(t('booking.invalidTime'));
        return;
      }
      
      const startTotal = startHours * 60 + startMinutes;
      const endTotal = endHours * 60 + endMinutes;
      
      // Если время окончания меньше времени начала, это означает переход через полночь
      // Например: 23:00 - 00:01 (начало 23:00, окончание на следующий день 00:01)
      // В этом случае это валидно, так как событие переходит на следующий день
      // Но если время одинаковое или очень близкое (например, 23:00 - 23:00), это ошибка
      if (startTotal === endTotal) {
        setError(t('booking.startTimeMustBeBeforeEndTime'));
        return;
      }
      
      // Если endTotal < startTotal, это переход через полночь - это валидно
      // Если endTotal > startTotal, это обычный случай в пределах одного дня - тоже валидно
      // Единственный невалидный случай - когда время одинаковое (уже проверено выше)
    }

    try {
      setSubmitting(true);
      
      // Проверяем доступность еще раз перед отправкой
      const availabilityResponse = await axios.get(`${API_BASE_URL}/api/bookings/availability`, {
        params: { 
          date: selectedDate, 
          timeStart: selectedTimeStart,
          timeEnd: selectedTimeEnd
        }
      });

      if (!availabilityResponse.data.available) {
        setError(t('booking.noTimeSlots'));
        setSubmitting(false);
        return;
      }

      // Создаем бронирование
      const response = await axios.post(`${API_BASE_URL}/api/bookings`, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        date: selectedDate,
        timeStart: selectedTimeStart,
        timeEnd: selectedTimeEnd,
        project: formData.project,
        location: formData.location.trim(),
        message: formData.message.trim()
      });

      if (response.data.success) {
        // Очищаем форму
        setFormData({
          name: '',
          email: '',
          phone: '',
          project: 'commercial',
          location: '',
          message: ''
        });
        
        // Вызываем callback успеха
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      setError(
        error.response?.data?.error || 
        t('contacts.sendMessage') + ' ' + t('contacts.sending')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = () => {
    // Парсим дату напрямую из строки - НЕ используем методы Date для получения компонентов
    // selectedDate всегда приходит в формате "YYYY-MM-DD" (строка)
    if (!selectedDate) return '';
    
    const dateParts = selectedDate.split('-');
    if (dateParts.length !== 3) return '';
    
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10); // месяц 1-12
    const day = parseInt(dateParts[2], 10);
    
    // Проверяем валидность
    if (isNaN(year) || isNaN(month) || isNaN(day)) return '';
    
    // Для определения дня недели создаем дату с полднем (12:00) в локальном времени
    // Используем конструктор Date(year, month, day, hours, minutes, seconds, milliseconds)
    // который всегда создает дату в ЛОКАЛЬНОМ времени
    const dateForWeekday = new Date(year, month - 1, day, 12, 0, 0, 0);
    const weekdayIndex = dateForWeekday.getDay();
    
    // Проверяем, что дата создана правильно - день должен совпадать
    // Если не совпадает, значит была проблема с созданием даты
    if (dateForWeekday.getDate() !== day || dateForWeekday.getMonth() !== month - 1 || dateForWeekday.getFullYear() !== year) {
      console.error('Date mismatch:', { year, month, day, created: dateForWeekday });
    }
    
    const weekdays = language === 'en' 
      ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      : ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
    
    const months = language === 'en'
      ? ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      : ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
    
    // Используем компоненты НАПРЯМУЮ из selectedDate - НЕ из объекта Date!
    const weekday = weekdays[weekdayIndex];
    const monthName = months[month - 1]; // month из selectedDate (1-12), поэтому month - 1 для индекса массива (0-11)
    const dayNum = day; // Используем day НАПРЯМУЮ из selectedDate - это ключевой момент!
    const yearNum = year; // Используем year НАПРЯМУЮ из selectedDate
    
    // Форматируем время из selectedTimeStart
    if (!selectedTimeStart) return '';
    const [startHours, startMinutes] = selectedTimeStart.split(':').map(Number);
    const localeCode = language === 'en' ? 'en-US' : 'ru-RU';
    
    // Форматируем время отдельно
    const timeDate = new Date(2000, 0, 1, startHours, startMinutes, 0, 0);
    const timeOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: localeCode === 'en-US'
    };
    const formattedTime = timeDate.toLocaleTimeString(localeCode, timeOptions);
    
    let formatted = language === 'en'
      ? `${weekday}, ${monthName} ${dayNum}, ${yearNum} at ${formattedTime}`
      : `${weekday}, ${dayNum} ${monthName} ${yearNum} в ${formattedTime}`;
    
    if (selectedTimeEnd) {
      formatted += ` - ${selectedTimeEnd}`;
    }
    
    return formatted;
  };

  return (
    <motion.div
      className="booking-form-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="booking-summary">
        <h3>{t('booking.bookingDetails')}</h3>
        <div className="summary-item">
          <FaCalendarAlt />
          <span>{formatDateTime()}</span>
        </div>
        <div className="summary-item">
          <FaClock />
          <span>{selectedTimeStart} - {selectedTimeEnd}</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="booking-form">
        {error && (
          <motion.div
            className="form-error"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {error}
          </motion.div>
        )}

        <div className="form-group">
          <label htmlFor="name">
            <FaUser /> {t('booking.name')} <span className="required">{t('booking.required')}</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={t('contacts.yourName')}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">
            <FaEnvelope /> {t('booking.email')} <span className="required">{t('booking.required')}</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="phone">
            <FaPhone /> {t('booking.phone')}
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+7 (999) 123-45-67"
          />
        </div>

        <div className="form-group">
          <label htmlFor="project">
            <FaCompactDisc /> {t('booking.project')} <span className="required">{t('booking.required')}</span>
          </label>
          <select
            id="project"
            name="project"
            value={formData.project}
            onChange={handleChange}
            required
            className="project-select"
          >
            <option value="commercial">{t('booking.projectCommercial')}</option>
            <option value="underground">{t('booking.projectUnderground')}</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="location">
            <FaMapMarkerAlt /> {t('booking.locationField')}
          </label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder={t('booking.locationDesc')}
          />
        </div>

        <div className="form-group">
          <label htmlFor="message">
            <FaComment /> {t('booking.message')}
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder={t('booking.message')}
            rows="5"
          />
        </div>

        <button
          type="submit"
          className="submit-booking-btn"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <FaSpinner className="spinning" /> {t('booking.submitting')}
            </>
          ) : (
            <>
              <FaPaperPlane /> {t('booking.submit')}
            </>
          )}
        </button>

        <p className="form-note">
          {t('booking.formNote')}
        </p>
      </form>
    </motion.div>
  );
};

export default BookingForm;
